/**
 * Column types for the collections edited through the collaboration hooks.
 *
 * A text input hands back a string — `<input type="number">` included — so an
 * edit to a numeric column arrives as `"1500"`, and an emptied input as `""`.
 * Appwrite validates the whole merged document on update and rejects either
 * with `Invalid document structure: Attribute "…" has invalid type. Value must
 * be a valid float`, so the write fails while the optimistic local state still
 * shows the new value: the edit looks saved until the page is reloaded.
 *
 * Coercing per column rather than "anything that looks like a number" matters:
 * `claimNumber` and `postcode` hold digits but are string columns, and must
 * stay strings on the wire.
 *
 * Mirrors the double/integer columns in `appwrite.config.json`. Adding a
 * numeric column that is edited inline means adding it here too.
 */
const NUMERIC_COLUMNS = {
  quote_requests: {
    excess: 'double',
    hourlyRate: 'double',
    vehicleValue: 'double',
    vehicleSumInsured: 'double',
    vehicleInsuranceExcess: 'double',
    totalImages: 'integer',
    processedImages: 'integer',
    damageAssessmentProgress: 'integer'
  },
  customer_vehicles: {
    year: 'integer',
    odometer: 'integer',
    value: 'double',
    sumInsured: 'double',
    insuranceExcess: 'double'
  },
  quotes: {
    subtotal: 'double',
    gst: 'double',
    total: 'double'
  }
};

/**
 * Coerce one value to the number Appwrite expects.
 *
 * Every numeric column on these collections is optional, so an empty input
 * clears the column with `null` rather than storing `0` — "no excess recorded"
 * and "a $0 excess" are different answers and the assessor picks between them.
 * A value that cannot be read as a number is treated the same way: there is no
 * number to store, and the alternative is a write that fails silently.
 *
 * @param {*} value - Raw value from the input (string, number, null…)
 * @param {'double'|'integer'} type - Column type from the schema
 * @returns {number|null} A finite number, or null to clear the column
 */
export function coerceNumericValue(value, type = 'double') {
  if (value === null || value === undefined || typeof value === 'boolean') return null;

  let numeric;
  if (typeof value === 'number') {
    numeric = value;
  } else {
    // Tolerate a pasted "$1,500.00" — a currency field invites one.
    const text = String(value).replace(/[$,\s]/g, '');
    // `Number('')` is 0, so an emptied input has to be caught before the parse
    // or clearing the field would silently store a zero.
    if (text === '') return null;
    numeric = Number(text);
  }

  if (!Number.isFinite(numeric)) return null;
  return type === 'integer' ? Math.round(numeric) : numeric;
}

/**
 * Coerce the numeric columns of an update payload, leaving everything else
 * untouched. Applied at the write boundary only, so what the user is typing
 * stays in local state exactly as typed.
 *
 * @param {string} collectionName - Collection name/ID being written to
 * @param {object} updates - Field/value pairs bound for `updateDocument`
 * @returns {object} The payload with numeric columns as numbers or null
 */
export function coerceFieldValues(collectionName, updates) {
  const numericColumns = NUMERIC_COLUMNS[collectionName];
  if (!numericColumns || !updates) return updates;

  let coerced = null;
  Object.entries(updates).forEach(([field, value]) => {
    const type = numericColumns[field];
    if (!type) return;
    const next = coerceNumericValue(value, type);
    if (next === value) return;
    if (!coerced) coerced = { ...updates };
    coerced[field] = next;
  });

  return coerced || updates;
}

export default coerceFieldValues;
